import { restGet, restPost } from "./restClient.js";
import { groupKeyId, newOperationId } from "./accessClient.js";
import { getAuthIdentityEpoch } from "./authTokenStore.js";

export const RAW_PROMPT_CAPABILITY = "ACTIVITY_CAPABILITY_VIEW_RAW_PROMPT_ACTIVITY";

export const ACTIVITY_CAPABILITIES = [
    {
        value: "ACTIVITY_CAPABILITY_VIEW_AGGREGATE_ACTIVITY",
        label: "View activity metadata",
        short: "Metadata",
        description: "Counts, trends, and individual activity metadata. Excludes prompt and response content.",
    },
    {
        value: RAW_PROMPT_CAPABILITY,
        label: "View prompts and responses",
        short: "Prompts and responses",
        description: "Prompt and response content, plus activity metadata, within the selected scope.",
    },
];

export function withActivityDisplayMetadata(page = {}) {
    const users = new Map((page.labels?.users ?? []).map(user => [user.userId, user.displayName]));
    const groups = new Map((page.labels?.groups ?? []).filter(item => item.group).map(item => [groupKeyId(item.group), item.displayName]));
    const group = key => ({ ...key, displayName: groups.get(groupKeyId(key)) || key.displayName || "" });
    const recipient = value => value?.directoryGroup ? { ...value, directoryGroup: group(value.directoryGroup) }
        : value?.viewerUserId ? { ...value, displayName: users.get(value.viewerUserId) || "" } : value;
    const grant = value => value?.scope?.groups ? { ...value, scope: { ...value.scope, groups: { groups: value.scope.groups.groups.map(group) } } } : value;
    return { ...page,
        ...(page.grants ? { grants: page.grants.map(row => ({ ...row, recipient: recipient(row.recipient), grant: grant(row.grant) })) } : {}),
    };
}

export async function listActivityGrants(afterId = "") {
    return withActivityDisplayMetadata(await restGet("/v1/access/activity-grants", { page_size: 25, after_id: afterId }));
}

export async function listViewerActivityGrants({ viewerUserId, pageSize = 25, pageToken = "" }) {
    const page = withActivityDisplayMetadata(await restGet("/v1/access/activity-grants", {
        viewer_user_id: viewerUserId, page_size: pageSize, page_token: pageToken,
    }));
    // Older servers ignore additive selectors. Never display an unfiltered
    // tenant page as the selected person's grants during a mixed-version deploy.
    if (!viewerUserId || (page.resolvedViewerUserId ?? page.resolved_viewer_user_id) !== viewerUserId) {
        throw new Error("The selected person's activity grants could not be confirmed.");
    }
    if ((page.grants ?? []).some(row => !ACTIVITY_CAPABILITIES.some(capability => capability.value === row.grant?.capability))) {
        throw new Error("The selected person's activity capabilities are not supported.");
    }
    const inheritance = page.inheritanceStatus ?? page.inheritance_status;
    const statuses = { GRANT_INHERITANCE_STATUS_RESOLVED: "resolved", GRANT_INHERITANCE_STATUS_VIEWER_UNMAPPED: "viewer_unmapped", GRANT_INHERITANCE_STATUS_NO_ACTIVE_GENERATION: "no_active_generation", 1: "resolved", 2: "viewer_unmapped", 3: "no_active_generation" };
    const enforcement = page.enforcementEnabled ?? page.enforcement_enabled;
    return { ...page, grants: page.grants ?? [], nextPageToken: page.nextPageToken ?? page.next_page_token ?? "",
        inheritanceStatus: statuses[inheritance] ?? "unknown",
        enforcementEnabled: typeof enforcement === "boolean" ? enforcement : null };
}

export function uncertainActivityMutation(error) {
    return error instanceof TypeError || error?.status === 408 || error?.status >= 500;
}

const MUTATIONS = {
    createGrant: "/v1/access/activity-grants",
    revokeGrant: "/v1/access/activity-grants:revoke",
};

// Retain this operation across an uncertain result. A retry must bind to the
// same payload and authority, even if the original mutation already committed.
export function activityConfigurationMutation(action, fields) {
    const path = MUTATIONS[action];
    if (!path) throw new Error("Unknown activity configuration action");
    const body = JSON.stringify({ ...fields, requestId: newOperationId() });
    const identity = getAuthIdentityEpoch();
    const assertCurrent = () => {
        if (identity !== getAuthIdentityEpoch()) {
            throw new DOMException("Your access changed. Review the assignment again.", "AbortError");
        }
    };
    const send = async () => {
        assertCurrent();
        const result = await restPost(path, JSON.parse(body));
        assertCurrent();
        return result;
    };
    return async () => {
        try { return await send(); }
        catch (error) {
            assertCurrent();
            if (!uncertainActivityMutation(error)) throw error;
            return send();
        }
    };
}

export function activityConfigurationError(error) {
    if (uncertainActivityMutation(error)) return "The result could not be confirmed. Retry to check the same change.";
    if (error?.status === 403) return "You do not have permission to manage activity grants.";
    if (error?.status === 409 || error?.grpcCode === "ABORTED") return "This assignment changed. Refresh the list and review it again.";
    if (error?.status === 404) return "This assignment or recipient is no longer available. Refresh the list and try again.";
    if (error?.status === 400) return "The change could not be saved. Review the recipient, activity capability, scope, and expiry.";
    return "The change could not be saved. Refresh the configuration and try again.";
}
